import { useCampaignStore } from '@/stores/campaignStore';
import { useNavigationStore } from '@/stores/navigationStore';
import { syncService } from '@/utils/syncService';
import { useCallback, useEffect } from 'react';

export const useCampaignContext = () => {
  const { getSelectedBranch, getSelectedCampaign } = useNavigationStore();
  const selectedBranch = getSelectedBranch();
  const selectedCampaign = getSelectedCampaign();
  // Use getState() to avoid subscription and prevent re-renders
  const getStore = () => useCampaignStore.getState();
  const getSyncService = () => {
    // Use the statically imported syncService
    return syncService;
  };
  
  // Note: No longer tracking context for auto-sync since it's now global

  // Set context when branch or campaign changes
  useEffect(() => {
    if (selectedBranch?.branch_short_name && selectedCampaign?.boundary_id) {
      getStore().syncWithNavigationStore();
    } else {
      getStore().clearCurrentContext();
    }
  }, [selectedBranch?.branch_short_name, selectedCampaign?.boundary_id]);

  // Note: Auto-sync is now managed globally and doesn't need to be started/stopped per context
  // The sync service will automatically handle all pending updates across all branches and campaigns

  // Enhanced voter update function that handles offline state
  const updateVoterData = useCallback(async (
    voterId: string, 
    updates: any, 
    syncImmediately: boolean = true
  ) => {
    // Ensure context is set before updating
    if (!selectedBranch?.branch_short_name || !selectedCampaign?.boundary_id) {
      console.warn('Context not set, cannot update voter data');
      return;
    }
    
    // Update local state immediately
    getStore().updateVoterData(voterId, updates);

    if (syncImmediately) {
      try {
        // Try to sync immediately if online
        const NetInfo = require('@react-native-community/netinfo').default;
        const networkState = await NetInfo.fetch();
        
        if (networkState.isConnected && networkState.isInternetReachable) {
          await getSyncService().syncPendingData();
        }
      } catch (error) {
        console.warn('Failed to sync voter update immediately:', error);
        // The update is still stored locally and will be synced later
      }
    }
  }, []); // Remove dependencies to prevent recreation

  // Enhanced address update function that handles offline state
  const updateAddressData = useCallback(async (
    addressId: string, 
    updates: any, 
    syncImmediately: boolean = true
  ) => {
    // Ensure context is set before updating
    if (!selectedBranch?.branch_short_name || !selectedCampaign?.boundary_id) {
      console.warn('Context not set, cannot update address data');
      return;
    }
    
    // Update local state immediately
    getStore().updateAddressData(addressId, updates);

    if (syncImmediately) {
      try {
        // Try to sync immediately if online
        const NetInfo = require('@react-native-community/netinfo').default;
        const networkState = await NetInfo.fetch();
        
        if (networkState.isConnected && networkState.isInternetReachable) {
          await getSyncService().syncPendingData();
        }
      } catch (error) {
        console.warn('Failed to sync address update immediately:', error);
        // The update is still stored locally and will be synced later
      }
    }
  }, []); // Remove dependencies to prevent recreation

  // Enhanced telling update function that handles offline state
  const updateTellingData = useCallback(async (
    voterId: string, 
    updates: any, 
    syncImmediately: boolean = true
  ) => {
    // Ensure context is set before updating
    if (!selectedBranch?.branch_short_name || !selectedCampaign?.boundary_id) {
      console.warn('Context not set, cannot update telling data');
      return;
    }
    
    // Update local state immediately
    getStore().updateTellingData(voterId, updates);

    if (syncImmediately) {
      try {
        // Try to sync immediately if online
        const NetInfo = require('@react-native-community/netinfo').default;
        const networkState = await NetInfo.fetch();
        
        if (networkState.isConnected && networkState.isInternetReachable) {
          await getSyncService().syncPendingData();
        }
      } catch (error) {
        console.warn('Failed to sync telling update immediately:', error);
        // The update is still stored locally and will be synced later
      }
    }
  }, []); // Remove dependencies to prevent recreation

  // Get voter data with offline updates merged
  const getVoterData = useCallback((voterId: string, baseVoter: any) => {
    const offlineUpdates = getStore().getVoterUpdates(voterId);
    if (offlineUpdates) {
      return { ...baseVoter, ...offlineUpdates };
    }
    return baseVoter;
  }, []); // Remove store dependency to prevent recreation

  // Get address data with offline updates merged
  const getAddressData = useCallback((addressId: string, baseAddress: any) => {
    const offlineUpdates = getStore().getAddressUpdates(addressId);
    if (offlineUpdates) {
      return { ...baseAddress, ...offlineUpdates };
    }
    return baseAddress;
  }, []); // Remove store dependency to prevent recreation

  // Get addresses for a specific engagement with offline updates merged
  const getAddressesForEngagement = useCallback((engagementId: string) => {
    return getStore().getAddressesForEngagement(engagementId);
  }, []); // Remove store dependency to prevent recreation

  // Manual sync function
  const syncData = useCallback(async () => {
    return await getSyncService().syncPendingData();
  }, []); // Remove syncService dependency to prevent recreation

  // Check if there are pending sync items
  const hasPendingSync = useCallback(() => {
    return getSyncService().hasPendingSync();
  }, []); // Remove syncService dependency to prevent recreation

  // Get pending sync count
  const getPendingSyncCount = useCallback(() => {
    return getSyncService().getPendingSyncCount();
  }, []); // Remove syncService dependency to prevent recreation

  // Cache management functions
  const getBattlePlans = useCallback(() => {
    return getStore().getBattlePlans();
  }, []);

  const getEngagements = useCallback((battlePlanId: string) => {
    return getStore().getEngagements(battlePlanId);
  }, []);

  const getEngagementMapDetails = useCallback((engagementId: string) => {
    return getStore().getEngagementMapDetails(engagementId);
  }, []);

  const checkAndUpdateCaches = useCallback(async () => {
    const branchId = selectedBranch?.branch_short_name;
    const campaignId = selectedCampaign?.boundary_id;
    
    if (!branchId || !campaignId) {
      return { success: false, updatedItems: 0, errors: ['No context set'] };
    }

    try {
      // Use store directly instead of cache service
      const store = getStore();
      const battlePlans = await store.fetchBattlePlans();
      return { success: true, updatedItems: battlePlans.length, errors: [] };
    } catch (error) {
      return { success: false, updatedItems: 0, errors: [`Cache update failed: ${error}`] };
    }
  }, [selectedBranch?.branch_short_name, selectedCampaign?.boundary_id]);



  return {
    
    // Data management
    updateVoterData,
    updateAddressData,
    updateTellingData,
    getVoterData,
    getAddressData,
    getAddressesForEngagement,
    
    // Cache management
    getBattlePlans,
    getEngagements,
    getEngagementMapDetails,
    checkAndUpdateCaches,
    
    // Sync management
    syncData,
    hasPendingSync,
    getPendingSyncCount,
    
    // Direct store access (for advanced use cases)
    store: getStore(),
  };
};
